import { ICellRendererParams, RowNode } from 'ag-grid-community';
import { html } from 'lit-element';
import { nothing, render } from 'lit-html';
import { get } from 'lodash';
import getString from './strings';

export interface ActionColumnRendererParams<TData = any, TValue = any>
  extends ICellRendererParams<TData, TValue> {
  isRowEditingEnabled: () => boolean;
  getOptions: () => {
    primaryKeyField: string;
    onEditRow: (node: RowNode<TData>) => unknown;
    onSaveRow: (rowId: string) => unknown;
    onCancelRow: (rowId: string) => unknown;
    customRenderer?: (data: TData) => unknown;
    isRowEditable?: (data: TData) => boolean;
    isRowDisabled?: (data: TData) => boolean;
  };
}

class ActionColumnRenderer<TData = any, TValue = any> {
  private eGui = document.createElement('div');
  private handleCancel?: () => void;
  private handleSave?: () => void;
  private handleEdit?: () => void;

  getGui() {
    return this.eGui;
  }

  init({
    getOptions,
    isRowEditingEnabled,
    ...params
  }: ActionColumnRendererParams<TData, TValue>) {
    this.eGui.classList.add(`action-row`);
    this.eGui.classList.add(`action-cell-container`);

    const options = getOptions();

    const showEditButtons =
      isRowEditingEnabled() &&
      (!options.isRowEditable || options.isRowEditable(params.data)) &&
      (!options.isRowDisabled || !options.isRowDisabled(params.data)) &&
      !params.node?.group;

    render(
      html`${options.customRenderer
        ? html` <div class="custom-user-content">
            ${options.customRenderer(params.data)}
          </div>`
        : nothing}
      ${showEditButtons
        ? html` <div class="edit-row-btns-container">
            <kat-button
              class="cancel"
              label="${getString('dg-cancel')}"
              variant="secondary"
              size="small"
            ></kat-button>
            <kat-button
              class="save"
              label="${getString('dg-save')}"
              variant="primary"
              size="small"
            ></kat-button>
            <kat-button
              class="edit"
              label="${getString('dg-edit')}"
              variant="secondary"
              size="small"
            ></kat-button>
          </div>`
        : nothing}`,
      this.eGui
    );

    this._removeEventListeners();

    const cancelButton = this.eGui.querySelector('.cancel');
    const saveButton = this.eGui.querySelector('.save');
    const editButton = this.eGui.querySelector('.edit');

    // this needs to be here to pull the correct `primaryKeyField` and
    const getRowId = () => get(params.data, options.primaryKeyField);

    // add event listeners if relevant
    if (showEditButtons) {
      this.handleCancel = () => options.onCancelRow(getRowId());
      this.handleSave = () => options.onSaveRow(getRowId());
      this.handleEdit = () => options.onEditRow(params.node);

      cancelButton.addEventListener('click', this.handleCancel);
      saveButton.addEventListener('click', this.handleSave);
      editButton.addEventListener('click', this.handleEdit);
    }
  }

  private _removeEventListeners() {
    const cancelButton = this.eGui.querySelector('.cancel');
    const saveButton = this.eGui.querySelector('.save');
    const editButton = this.eGui.querySelector('.edit');

    // remove old event listeners if any
    if (cancelButton && this.handleCancel) {
      cancelButton.removeEventListener('click', this.handleCancel);
    }
    if (saveButton && this.handleSave) {
      saveButton.removeEventListener('click', this.handleSave);
    }
    if (editButton && this.handleEdit) {
      saveButton.removeEventListener('click', this.handleEdit);
    }
  }

  destroy() {
    this._removeEventListeners();
  }
}

export { ActionColumnRenderer };
